/**
 * /app/financial — Financial Planning hub.
 *
 * Composed of small reusable components. Each section is collapsible,
 * gated by entitlements, and reads/writes through FinancialPlanContext.
 */
import PageHeader from "@/components/PageHeader";
import { FinancialPlanProvider, useFinancialPlan } from "@/context/FinancialPlanContext";
import { Section } from "@/components/financial/primitives";
import AIFinancialAdvisor from "@/components/financial/AIFinancialAdvisor";
import NetWorthCard from "@/components/financial/NetWorthCard";
import MultiCurrencySummary from "@/components/financial/MultiCurrencySummary";
import EmergencyFund from "@/components/financial/EmergencyFund";
import DebtManager from "@/components/financial/DebtManager";
import SavingsGoals from "@/components/financial/SavingsGoals";
import InvestmentsManager from "@/components/financial/InvestmentsManager";
import RetirementPlannerV2 from "@/components/financial/RetirementPlannerV2";
import ProjectionsSection from "@/components/financial/ProjectionsSection";
import PropertiesManager from "@/components/financial/PropertiesManager";
import { IncomeSources, ExpenseCategories, Assumptions } from "@/components/financial/IncomeExpensesAssumptions";

export default function Financial() {
  return (
    <FinancialPlanProvider>
      <FinancialInner />
    </FinancialPlanProvider>
  );
}

function FinancialInner() {
  const { loading } = useFinancialPlan();
  return (
    <div data-testid="financial-page">
      <PageHeader
        eyebrow="Module 5"
        title="Financial planning"
        subtitle="Debts, savings, investments, retirement, and AI-driven strategy — reasoned across your whole picture."
      />
      {loading ? (
        <div className="text-sm text-muted-foreground py-8">Loading your plan…</div>
      ) : (
        <>
          <AIFinancialAdvisor />
          <NetWorthCard />
          <MultiCurrencySummary />

          <Section title="Income sources" subtitle="Where your money comes from — salary, bonus, side hustles, passive income." testId="section-income" defaultOpen={true}>
            <IncomeSources />
          </Section>

          <Section title="Expense categories" subtitle="Where your money goes — used to compute savings rate + FI target." testId="section-expenses" defaultOpen={false}>
            <ExpenseCategories />
          </Section>

          <Section title="Emergency fund" subtitle="Your safety net — target is 3-6 months of expenses." testId="section-emergency" defaultOpen={true}>
            <EmergencyFund />
          </Section>

          <Section
            title="Properties owned"
            subtitle="Homes, rentals, land — with mortgages, expenses, and cash flow. Feeds your net worth."
            testId="section-properties"
            defaultOpen={true}
          >
            <PropertiesManager />
          </Section>

          <Section
            title="Debt manager"
            subtitle="Track balances, rates, and simulate Avalanche vs Snowball payoff."
            entitlementKey="financial_v2_debt_manager"
            tier="Pro"
            upsellPitch="Multi-debt tracking with rate-aware payoff simulator saves the average user thousands in interest — a Pro-tier signature feature."
            testId="section-debts"
            defaultOpen={true}
          >
            <DebtManager />
          </Section>

          <Section
            title="Savings goals"
            subtitle="Track goals per category — vacation, home, education, etc."
            entitlementKey="financial_v2_savings_goals"
            tier="Pro"
            upsellPitch="Multiple named goals with per-category progress. Pro unlocks unlimited buckets."
            testId="section-savings"
            defaultOpen={false}
          >
            <SavingsGoals />
          </Section>

          <Section
            title="Investments"
            subtitle="401(k), IRA, TFSA, RRSP, brokerage, crypto — all in one view."
            entitlementKey="financial_v2_investments_multi"
            tier="Pro"
            upsellPitch="Multi-account view with expected returns and employer match — feeds retirement projections."
            testId="section-investments"
            defaultOpen={false}
          >
            <InvestmentsManager />
          </Section>

          <Section
            title="Lifetime projections & scenarios"
            subtitle="Deterministic engine → lifetime earnings, scenario compare, FIRE, home planner, career ROI."
            testId="section-projections"
            defaultOpen={true}
          >
            <ProjectionsSection />
          </Section>

          <Section
            title="Retirement planner"
            subtitle="Readiness score + projected nest egg + gap math."
            entitlementKey="financial_v2_retirement_v2"
            tier="Pro"
            upsellPitch="Real retirement readiness with government pensions, withdrawal-rate math, and a required-savings solver."
            testId="section-retirement"
            defaultOpen={true}
          >
            <RetirementPlannerV2 />
          </Section>

          <Section
            title="Assumptions"
            subtitle="Inflation, expected return, tax country. Powers every projection."
            testId="section-assumptions"
            defaultOpen={false}
          >
            <Assumptions />
          </Section>
        </>
      )}
    </div>
  );
}
